import React from 'react';
import {
    COUNTDOWN_EVENT_VARIABLE,
    DISCOUNTED_MONTHS_VARIABLE,
    DISCOUNT_PRICE_VARIABLE,
    FREE_TRIAL_VARIABLE,
    IAddon,
    IBannerAction,
    IBannerSlide,
    IBannerSlider,
    IChargeTier,
    IOffer,
    IOffers,
    PRICE_VARIABLE,
    Promotion,
    REM_MONTHS_VARIABLE,
    SAVING_PRICE_VARIABLE
} from '@dazn/lp-web/src/types';
import localisePrice from '@dazn/lp-web/src/utils/localisePrice';
import {
    getCountry,
    getCurrency,
    getLanguage
} from '@dazn/lp-web/src/utils/dazn/Region';
import {
    getPpvDefaultDiscountPercentage,
    getPpvSpecificDiscountPercentage,
    ringPassDiscountEnabled,
    disabledDiscountedPpv
} from '@dazn/lp-web/src/features/getRingPassDiscount';
import {getUserStatus} from '@dazn/lp-web/src/utils';
import {
    filterDiscountedOffers,
    getOfferChargeTier
} from '@dazn/lp-web/src/components/ContentTiers/contentTiersUtils';
import {bannerTypes} from '@dazn/lp-web/src/constants/bannerTypes.constants';
import {isMultiPromoDiscountsEnabled} from '@dazn/lp-web/src/contexts/LandingPageContext';
import format, {dateMapDistanceToNow} from '@dazn/fe-formats';
import {getConcurrentPpvMainEventTime} from '../../features/getPpvMainEventTme';
import {getDictionaryString} from '../../utils/dictionary';
import {isPurchased} from '../VideoPlayer/video-player.utils';

interface IGetBanners {
    offers: IOffers | null;
    promoOffers: IOffers | null;
    multiplePromoOffers: IOffers[] | null;
    data: IBannerSlider;
}

interface OfferPrice {
    chargeTiers: IChargeTier[];
    currency: string;
}

const getLabel = (banner: any, offer: any, isButton = false) => {
    const {label, freeTrialLabel} = banner;
    const totalFreeDays = offer?.TotalFreeDays ?? 0;
    let finalLabel = isButton ? freeTrialLabel : label;
    const countDownDate = banner?.countdownTime
        ? new Date(banner?.countdownTime)
        : null;
    if (finalLabel?.includes(FREE_TRIAL_VARIABLE) && totalFreeDays) {
        return finalLabel?.replace(FREE_TRIAL_VARIABLE, String(totalFreeDays));
    }
    const formatValue = format({
        country: getCountry(),
        language: getLanguage()
    });

    if (finalLabel?.includes(COUNTDOWN_EVENT_VARIABLE) && countDownDate) {
        const countDownTime =
            countDownDate &&
            formatValue(countDownDate).pipe(
                dateMapDistanceToNow({
                    moreThan7DaysAgo: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_MoreThanWeekAgo'
                        )
                    },
                    between2And6DaysAgo: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_LessThanWeekAgo'
                        )
                    },
                    yesterday: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_Yesterday'
                        )
                    },
                    lastNight: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_LastNight'
                        )
                    },
                    today: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_Today'
                        )
                    },
                    thisEvening: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_ThisEvening'
                        )
                    },
                    tonight: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_Tonight'
                        )
                    },
                    tomorrow: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_Tomorrow'
                        )
                    },
                    between2And6Days: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_LessThanWeekAway'
                        )
                    },
                    moreThan7Days: {
                        value: getDictionaryString(
                            'addon_eventStartDateTime_MoreThanWeekAway'
                        )
                    }
                })
            );
        return countDownTime
            ? finalLabel?.replace(COUNTDOWN_EVENT_VARIABLE, countDownTime)
            : finalLabel;
    }

    if (label?.includes('{event}')) {
        try {
            const eventDate = offer?.EventStartDate
                ? new Date(offer.EventStartDate)
                : null;

            const eventTime =
                eventDate &&
                formatValue(eventDate).pipe(
                    dateMapDistanceToNow({
                        moreThan7DaysAgo: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_MoreThanWeekAgo'
                            )
                        },
                        between2And6DaysAgo: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_LessThanWeekAgo'
                            )
                        },
                        yesterday: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_Yesterday'
                            )
                        },
                        lastNight: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_LastNight'
                            )
                        },
                        today: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_Today'
                            )
                        },
                        thisEvening: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_ThisEvening'
                            )
                        },
                        tonight: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_Tonight'
                            )
                        },
                        tomorrow: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_Tomorrow'
                            )
                        },
                        between2And6Days: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_LessThanWeekAway'
                            )
                        },
                        moreThan7Days: {
                            value: getDictionaryString(
                                'addon_eventStartDateTime_MoreThanWeekAway'
                            )
                        }
                    })
                );
            return eventTime ? label?.replace('{event}', eventTime) : '';
        } catch (e) {
            return '';
        }
    }

    if (label?.includes('{eventDetails}')) {
        const eventDate = offer?.EventStartDate
            ? new Date(offer.EventStartDate)
            : null;
        const mainEventDateAndTime = offer?.EventStartDate
            ? new Date(offer?.EventStartDate)
            : null;
        let ppvMainEventTimeConcurrentHours =
            getConcurrentPpvMainEventTime() ?? {};

        if (
            ppvMainEventTimeConcurrentHours != '' &&
            typeof ppvMainEventTimeConcurrentHours === 'string'
        ) {
            try {
                ppvMainEventTimeConcurrentHours = JSON.parse(
                    ppvMainEventTimeConcurrentHours
                );
            } catch (e) {
                //Silently Fail
            }
        }

        const ppvMainEventTimeHours = ppvMainEventTimeConcurrentHours[
            banner?.entitlementSetId
        ]
            ? ppvMainEventTimeConcurrentHours[banner?.entitlementSetId]
            : 0;
        mainEventDateAndTime?.setHours(
            mainEventDateAndTime.getHours() + ppvMainEventTimeHours
        );
        const isNextDayMainEvent =
            eventDate?.getDay() != mainEventDateAndTime?.getDay();

        try {
            const currentDateTimeString =
                eventDate &&
                formatValue(eventDate).pipe(
                    dateMapDistanceToNow({
                        moreThan7DaysAgo: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_MoreThanWeekAgo'
                            )
                        },
                        between2And6DaysAgo: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LessThanWeekAgo'
                            )
                        },
                        yesterday: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Yesterday'
                            )
                        },
                        lastNight: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LastNight'
                            )
                        },
                        today: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Today'
                            )
                        },
                        thisEvening: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_ThisEvening'
                            )
                        },
                        tonight: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Tonight'
                            )
                        },
                        tomorrow: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Tomorrow'
                            )
                        },
                        between2And6Days: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LessThanWeekAway'
                            )
                        },
                        moreThan7Days: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_MoreThanWeekAway'
                            )
                        }
                    })
                );

            const formattedDateTimeString =
                mainEventDateAndTime &&
                formatValue(mainEventDateAndTime).pipe(
                    dateMapDistanceToNow({
                        moreThan7DaysAgo: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_MoreThanWeekAgo' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        between2And6DaysAgo: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LessThanWeekAgo' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        yesterday: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Yesterday' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        lastNight: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LastNight' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        today: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Today' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        thisEvening: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_ThisEvening' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        tonight: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Tonight' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        tomorrow: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_Tomorrow' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        between2And6Days: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_LessThanWeekAway' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        },
                        moreThan7Days: {
                            value: getDictionaryString(
                                'addon_maineventStartDateTime_MoreThanWeekAway' +
                                    (isNextDayMainEvent
                                        ? '_nextDay'
                                        : '_sameDay')
                            )
                        }
                    })
                );
            return (
                currentDateTimeString &&
                formattedDateTimeString &&
                currentDateTimeString + formattedDateTimeString
            );
        } catch (e) {
            return '';
        }
    }

    return finalLabel;
};

const getButtonLabel = (
    action: any,
    offer: any,
    banner?: IBannerSlide
): string => {
    const price = offer ? getPrice(offer) : '';
    const discountPrice = banner ? getDiscountPrice(offer, banner) : null;
    const totalFreeDays = offer?.TotalFreeDays || 0;
    const freeTrialText =
        totalFreeDays > 0 ? getLabel(action, offer, true) : action?.label || '';

    if (!freeTrialText) return '';

    if (freeTrialText?.includes(PRICE_VARIABLE) && (discountPrice || price)) {
        return freeTrialText?.replace(PRICE_VARIABLE, discountPrice || price);
    }

    if (freeTrialText?.includes('##')) {
        return freeTrialText?.replace(
            /##(.*?)##/g,
            '<span style="font-weight: 800">$1</span>'
        );
    }

    return freeTrialText;
};

function getNearestPromotion(
    eligibalPromotions: Promotion[],
    discountPercentage: number
) {
    if (!eligibalPromotions?.length || !discountPercentage) {
        return;
    }

    return eligibalPromotions?.reduce((nearest, current) => {
        const nearestDiff =
            nearest?.DiscountPercentage &&
            Math.abs(nearest?.DiscountPercentage - discountPercentage);
        const currentDiff =
            current?.DiscountPercentage &&
            Math.abs(current?.DiscountPercentage - discountPercentage);

        return currentDiff && nearestDiff && currentDiff < nearestDiff
            ? current
            : nearest;
    });
}

export function getPpvDiscountPrice(offer: IAddon | null): string | null {
    if (!offer) return null;

    if (disabledDiscountedPpv()?.includes(offer?.EntitlementSetId)) {
        return null;
    }

    const eligiblePromotions = offer?.Promotions?.filter(
        (promotion: Promotion) => {
            return promotion?.Conditions?.filter((condition) => {
                if (getUserStatus() === 'Partial') {
                    return (
                        condition?.DenyReasons?.includes('NEEDS_BUNDLE') ||
                        condition?.DenyReasons?.length === 0
                    );
                }

                return (
                    condition?.DenyReasons === null ||
                    condition?.DenyReasons.length === 0
                );
            });
        }
    );
    const ppvSpecificDiscountPercentage: {[key: string]: number} =
        getPpvSpecificDiscountPercentage();
    const ppvDefaultDiscountPercentage = getPpvDefaultDiscountPercentage();
    const expectedDiscountPercentage =
        ppvSpecificDiscountPercentage[offer?.EntitlementSetId] ??
        ppvDefaultDiscountPercentage;
    const expectedPromotion = getNearestPromotion(
        eligiblePromotions,
        expectedDiscountPercentage
    );

    if (expectedPromotion) {
        return localisePrice(expectedPromotion?.DiscountAmount);
    }

    return null;
}

function getPriceStrings(priceText: string, variable: string, price?: string) {
    const prefixEnd = priceText?.indexOf(variable);
    const suffixStart = prefixEnd + variable.length;
    const prefix = priceText?.substring(0, prefixEnd);
    let suffix = priceText?.substring(suffixStart);
    if (suffix.includes(PRICE_VARIABLE) && price) {
        suffix = suffix.replace(PRICE_VARIABLE, price);
    }

    return {prefix, suffix};
}

function getTierDiscountPrice(offer: IOffer | null): string | null {
    const chargeTier = getOfferChargeTier(offer);
    const discountedAmount = chargeTier?.Discount?.Price;

    if (discountedAmount) {
        return localisePrice(discountedAmount);
    }

    return null;
}

function getDiscountPrice(offer: IOffer | IAddon | null, banner: IBannerSlide) {
    if (banner?.bannerType === bannerTypes.PPV && ringPassDiscountEnabled()) {
        return getPpvDiscountPrice(offer as IAddon);
    }

    if (banner?.bannerType === bannerTypes.TIER) {
        return getTierDiscountPrice(offer as IOffer);
    }

    return null;
}

function getDiscountedMonths(
    offer: IOffer | null,
    banner: IBannerSlide
): number | null {
    if (banner?.bannerType !== bannerTypes.TIER) {
        return null;
    }

    const chargeTier = offer ? getOfferChargeTier(offer as IOffer) : null;

    return chargeTier?.Discount?.DiscountedMonths || null;
}

function getRemainingMonths(
    offer: IOffer | null,
    banner: IBannerSlide
): number | null {
    if (
        banner?.bannerType !== bannerTypes.TIER ||
        !offer?.Instalment?.TermInMonths
    ) {
        return null;
    }

    const {TermInMonths: totalInstalledMonths} = offer.Instalment;
    const chargeTier = getOfferChargeTier(offer);
    const discountedMonths = chargeTier?.Discount?.DiscountedMonths || 0;

    return totalInstalledMonths - discountedMonths;
}

function getOfferPrice({chargeTiers, currency}: OfferPrice): {
    Price: number | null;
    Currency: string | null;
} {
    if (!chargeTiers || chargeTiers?.length === 0) {
        return {Price: null, Currency: null};
    }

    const offerPricingForCountry = chargeTiers?.find(
        ({Currency}) => Currency === currency?.toUpperCase()
    );

    const chargeTier = offerPricingForCountry || chargeTiers?.[0];

    return {
        Price: chargeTier.Price,
        Currency: chargeTier.Currency
    };
}

function getPrice(offer: any) {
    const {Price} = getOfferPrice({
        chargeTiers: offer?.ChargeTiers,
        currency: getCurrency()
    });

    return localisePrice(Price);
}

function getPriceAmount(offer: any) {
    const {Price} = getOfferPrice({
        chargeTiers: offer?.ChargeTiers,
        currency: getCurrency()
    });

    return Price;
}

function getDiscountAmount(offer: any) {
    const chargeTier = getOfferChargeTier(offer);
    const discountedAmount = chargeTier?.Discount?.Price;

    return discountedAmount;
}

function getSavingPrice(offer: any) {
    const priceAmount = getPriceAmount(offer);
    const discountAmount = getDiscountAmount(offer);

    if (priceAmount && discountAmount) {
        return localisePrice(priceAmount - discountAmount);
    }

    return null;
}

function getTitle(banner: IBannerSlide, offer: IOffer | null) {
    const price = getPrice(offer);
    const discountPrice = getDiscountPrice(offer, banner);
    const savingPrice = getSavingPrice(offer);

    if (banner?.title?.includes(SAVING_PRICE_VARIABLE) && savingPrice) {
        return banner?.title?.replace(SAVING_PRICE_VARIABLE, savingPrice);
    }

    if (banner?.title?.includes(PRICE_VARIABLE) && (discountPrice || price)) {
        return banner?.title?.replace(PRICE_VARIABLE, discountPrice || price);
    }

    return banner?.title;
}
function getDescription(banner: IBannerSlide, offer: IOffer | null) {
    const price = getPrice(offer);
    const discountPrice = getDiscountPrice(offer, banner);
    const savingPrice = getSavingPrice(offer);

    if (banner?.description?.includes(SAVING_PRICE_VARIABLE) && savingPrice) {
        return banner?.description?.replace(SAVING_PRICE_VARIABLE, savingPrice);
    }

    if (
        banner?.description?.includes(PRICE_VARIABLE) &&
        (discountPrice || price)
    ) {
        return banner?.description?.replace(
            PRICE_VARIABLE,
            discountPrice || price
        );
    }

    return banner?.description;
}
function getOfferLabel(banner: IBannerSlide, offer: IOffer | null) {
    if (!banner?.offerLabel || !offer) {
        return '';
    }

    let offerLabel = banner?.offerLabel || '';

    const variableReplacements: {[key: string]: string | null} = {
        [DISCOUNTED_MONTHS_VARIABLE]:
            getDiscountedMonths(offer, banner)?.toString() || null,
        [PRICE_VARIABLE]: getPrice(offer) || null,
        [DISCOUNT_PRICE_VARIABLE]: getDiscountPrice(offer, banner) || null,
        [REM_MONTHS_VARIABLE]:
            getRemainingMonths(offer, banner)?.toString() || null
    };

    for (const variable in variableReplacements) {
        const value = variableReplacements[variable];
        if (value && offerLabel.includes(variable)) {
            offerLabel = offerLabel.replace(variable, value);
        }
    }

    return offerLabel;
}

function getMobileButtonLabel(
    action: IBannerAction,
    banner: IBannerSlide,
    offer: IOffer | null
) {
    const price = getPrice(offer);
    const discountPrice = getDiscountPrice(offer, banner);
    const {label, mobileButtonLabel} = action;
    const freeTrialText = getButtonLabel(action, offer) ?? '';
    if (!mobileButtonLabel) return freeTrialText ? freeTrialText : label;

    if (
        mobileButtonLabel?.includes(PRICE_VARIABLE) &&
        (discountPrice || price || freeTrialText)
    ) {
        return freeTrialText
            ? freeTrialText?.replace(PRICE_VARIABLE, discountPrice || price)
            : mobileButtonLabel?.replace(
                  PRICE_VARIABLE,
                  discountPrice || price
              );
    }

    return freeTrialText ? freeTrialText : mobileButtonLabel;
}

export function getFinalPrice(banner: any, offer: any): any {
    const price = getPrice(offer);
    const discountPrice = getDiscountPrice(offer, banner);
    const priceText = banner?.price;
    const discountPriceText = banner?.discountPrice;

    if (discountPriceText && discountPrice) {
        if (
            discountPriceText?.includes(DISCOUNT_PRICE_VARIABLE) &&
            discountPrice
        ) {
            const {prefix, suffix} = getPriceStrings(
                discountPriceText,
                DISCOUNT_PRICE_VARIABLE,
                price
            );

            return (
                <>
                    {prefix}
                    <del>{price}</del> <span>{discountPrice}</span>
                    {suffix}
                </>
            );
        }

        if (discountPriceText?.includes(PRICE_VARIABLE) && discountPrice) {
            const {prefix, suffix} = getPriceStrings(
                discountPriceText,
                PRICE_VARIABLE,
                price
            );

            return (
                <>
                    {prefix}
                    <span>{discountPrice}</span>
                    {suffix}
                </>
            );
        }

        return banner?.discountPrice;
    }

    if (priceText) {
        if (priceText?.includes(PRICE_VARIABLE) && price) {
            const {prefix, suffix} = getPriceStrings(priceText, PRICE_VARIABLE);

            return (
                <>
                    {prefix}
                    <span>{price}</span>
                    {suffix}
                </>
            );
        }

        return banner?.price;
    }

    return null;
}

export function parseEntitlement(
    input: string
): {entitlementSetId: string; autoRenew: boolean | null} | string {
    if (!input.includes('#')) return input; // Return null if '#' is not present

    const [entitlementSetIdPart, autoRenewPart] = input.split('#');

    const entitlementSetId = entitlementSetIdPart.trim();
    const autoRenewMatch = autoRenewPart.match(/autoRenew:(true|false)/);

    if (!autoRenewMatch) return {entitlementSetId, autoRenew: null}; // If autoRenew is not found, return null for it

    const autoRenew = autoRenewMatch[1] === 'true';

    return {entitlementSetId, autoRenew};
}

function getOffer(
    offers: IOffers | null,
    promoOffers: IOffers | null,
    banner: IBannerSlide,
    multiplePromoOffers: IOffers[] | null
): IOffer | IAddon | undefined {
    const hasAutoRenew = banner?.entitlementSetId?.includes('#autoRenew');
    const result =
        banner?.entitlementSetId && parseEntitlement(banner?.entitlementSetId);
    const entitlementSetId =
        typeof result === 'string' ? result : result?.entitlementSetId;
    const autoRenew = typeof result === 'string' ? null : result?.autoRenew;
    function finder(offer: any) {
        if (banner?.billingPeriod) {
            return hasAutoRenew
                ? offer?.EntitlementSetId === entitlementSetId &&
                      offer?.BillingPeriod === banner?.billingPeriod &&
                      offer?.AutoRenew === autoRenew
                : offer?.EntitlementSetId === entitlementSetId &&
                      offer?.BillingPeriod === banner?.billingPeriod;
        }

        return offer?.EntitlementSetId === entitlementSetId;
    }

    const filteredData = multiplePromoOffers
        ? multiplePromoOffers
              ?.map((obj: IOffers) => {
                  const filteredOffers =
                      obj && obj?.Offers && obj?.Offers?.length > 0
                          ? obj?.Offers?.filter(finder)
                          : [];
                  return {
                      ...obj,
                      Offers: filteredOffers
                  };
              })
              .filter((obj) => obj?.Offers?.length > 0)
        : [];

    const finalPromoOffer =
        isMultiPromoDiscountsEnabled && filteredData
            ? filterDiscountedOffers(filteredData)
            : promoOffers;

    if (
        (isMultiPromoDiscountsEnabled && filteredData) ||
        (promoOffers?.Offers && promoOffers.Offers.length > 0)
    ) {
        const offer =
            finalPromoOffer?.Offers?.find(finder) ||
            finalPromoOffer?.Addons?.find(finder);
        if (offer) return offer;
    }

    return offers?.Offers?.find(finder) || offers?.Addons?.find(finder);
}

function getBanners({
    offers,
    data,
    promoOffers,
    multiplePromoOffers
}: IGetBanners) {
    const queryParams = Array.from(
        new URLSearchParams(new URL(window.location.href).search).keys()
    );

    const isNonVideoBanner = (banner: IBannerSlide) =>
        banner.backgroundType !== 'video' || !banner?.isVideoTagSpecific;

    const hasMatchingVideoTags = (
        banner: IBannerSlide,
        queryParams: string[]
    ) =>
        banner.backgroundType === 'video' &&
        banner?.isVideoTagSpecific &&
        banner?.videoTags?.some((tag) => queryParams.includes(tag));

    const shouldDisplayAction = (
        action: IBannerAction,
        banner: IBannerSlide,
        userProfile: Record<string, any>
    ): boolean => {
        const {buttonId = ''} = action;
        // Guest user logic: filter out specific actions
        if (
            Object.keys(userProfile).length === 0 &&
            buttonId === 'navigate_to_fixture'
        ) {
            return false;
        }

        // Authenticated user logic: check entitlement
        if (Object.keys(userProfile).length > 0) {
            const isEntitlementPurchased =
                (banner?.entitlementSetId &&
                    isPurchased(banner.entitlementSetId)) ||
                false;

            if (buttonId === 'navigate_to_fixture') {
                return isEntitlementPurchased;
            }
            return !isEntitlementPurchased;
        }

        // Default: keep the action
        return true;
    };

    const sortBannersByBackgroundType = (a: IBannerSlide, b: IBannerSlide) =>
        a.backgroundType === 'video'
            ? -1
            : b.backgroundType === 'video'
            ? 1
            : 0;

    return data?.banners
        ?.filter(
            (banner) =>
                isNonVideoBanner(banner) ||
                hasMatchingVideoTags(banner, queryParams)
        )
        ?.sort(sortBannersByBackgroundType)
        ?.map((banner) => {
            const offer: any = getOffer(
                offers,
                promoOffers,
                banner,
                multiplePromoOffers
            );
            const finalPrice = getFinalPrice(banner, offer);
            return {
                ...banner,
                title: getTitle(banner, offer),
                label: getLabel(banner, offer) ?? '',
                description: getDescription(banner, offer),
                offerLabel: getOfferLabel(banner, offer),
                price: finalPrice,
                actions: banner?.actions
                    ?.filter((action) =>
                        shouldDisplayAction(
                            action,
                            banner,
                            window.dazn.userProfile
                        )
                    )
                    ?.slice(0, 2)
                    .map((action) => ({
                        ...action,
                        mobileButtonLabel: getMobileButtonLabel(
                            action,
                            banner,
                            offer
                        ),
                        label: getButtonLabel(action, offer, banner) ?? ''
                    })),
                enabled:
                    banner?.bannerType !== bannerTypes.OTHERS
                        ? Boolean(offer)
                        : true
            };
        });
}

function getHiddenFields(hideFieldsInMobile?: string[]) {
    if (!hideFieldsInMobile?.length) {
        return null;
    }

    return {
        hideTitle: hideFieldsInMobile.includes('title'),
        hideDescription: hideFieldsInMobile.includes('description'),
        hideLogo: hideFieldsInMobile.includes('logo'),
        hidePrice: hideFieldsInMobile.includes('price')
    };
}

export {getBanners, getHiddenFields, getOfferPrice, getPriceStrings};
